import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonService } from '../../services';
import { BusinessService } from '../../services/business';
import { IBusiness, IBusinessResponse } from '../../models';
import { HttpErrorResponse } from '@angular/common/http';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { BytesToReadablePipe, DateFormatPipe, DaysToDurationPipe, DynamicHeight } from '../../helper';
import { CommonModule, UpperCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Constants } from '../../common';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-business',
  imports: [
    MatTableModule,
    DynamicHeight,
    MatIconModule,
    DateFormatPipe,
    UpperCasePipe,
    BytesToReadablePipe,
    DaysToDurationPipe,
    MatSortModule,
    CommonModule,
    FormsModule
  ],
  templateUrl: './business.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business.component.scss',
})
export class BusinessComponent implements OnInit {

  businessDetails: IBusiness[] = [];

  displayedColumns: string[] = ['serial_no', 'name', 'business_admin_email', 'status', 'created_at', 'expiry_date', 'expiry_status', 'members_limit', 'screen_limit', 'storage_limit', 'validity_period', 'action_button'];
  dataSource = new MatTableDataSource<IBusiness>([]);

  @ViewChild(MatSort) sort!: MatSort;

  filterValue: string = '';
  sortActive: string = '';
  sortDirection: SortDirection = '';
  
  constructor(public commonService: CommonService, private businessService: BusinessService, private router: Router) {

  }

  ngOnInit(): void {
    this.loadTableState();
    this.setupSortingAndFiltering();
    this.initializeBusinessList();
  }

  setupSortingAndFiltering() {
    this.dataSource.filterPredicate = (data: IBusiness, filter: string) => {
      const searchStr = filter.toLowerCase();
      const name = (data.name || '').toLowerCase();
      const email = (data.business_admin_email || '').toLowerCase();
      const status = (data.status || '').toLowerCase();
      const expiryStatusObj = this.getExpiryStatus(data.expiry_date);
      const expiryLabel = (expiryStatusObj?.label || '').toLowerCase();
      const screenLimit = String(data.screen_limit || '');
      const membersLimit = String(data.members_limit || '');
      
      return name.includes(searchStr) || 
             email.includes(searchStr) || 
             status.includes(searchStr) || 
             expiryLabel.includes(searchStr) ||
             screenLimit.includes(searchStr) ||
             membersLimit.includes(searchStr);
    };

    this.dataSource.sortingDataAccessor = (data: IBusiness, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'expiry_status':
          return this.getExpiryStatus(data.expiry_date)?.label || '';
        case 'created_at':
        case 'expiry_date':
          return data[sortHeaderId] ? new Date(data[sortHeaderId]).getTime() : 0;
        default:
          return (data as any)[sortHeaderId] ?? '';
      }
    };
  }

  private loadTableState() {
    const saved = localStorage.getItem(Constants.BUSINESS_TABLE_STATE_KEY);
    if (saved) {
      try {
        const state = JSON.parse(saved);
        this.filterValue = state.filter || '';
        this.sortActive = state.sortActive || '';
        this.sortDirection = (state.sortDirection as SortDirection) || '';
      } catch (e) {
        console.error('Failed to parse business table state', e);
      }
    }
  }

  private saveTableState() {
    const state = {
      filter: this.filterValue || '',
      sortActive: this.sortActive || '',
      sortDirection: this.sortDirection || ''
    };
    localStorage.setItem(Constants.BUSINESS_TABLE_STATE_KEY, JSON.stringify(state));
  }

  private applySavedState() {
    if (this.filterValue) {
      this.dataSource.filter = this.filterValue.trim().toLowerCase();
    }
    if (this.sortActive && this.sortDirection && this.sort) {
      this.sort.active = this.sortActive;
      this.sort.direction = this.sortDirection;
      this.dataSource.sort = this.sort;
    }
  }

  applyFilter(event: Event) {
    const filterVal = (event.target as HTMLInputElement).value;
    this.filterValue = filterVal;
    this.dataSource.filter = filterVal.trim().toLowerCase();
    this.saveTableState();
  }

  clearFilter() {
    this.filterValue = '';
    this.dataSource.filter = '';
    this.saveTableState();
  }

  onSortChange(sort: Sort) {
    this.sortActive = sort.active;
    this.sortDirection = sort.direction;
    this.saveTableState();
  }

  getTableHeight = () => Math.max(window.innerHeight - 130, 300) + 'px';

  initializeBusinessList() {
    this.commonService.showSpinner = true;
    this.businessService.getAllBusiness().subscribe({
      next: (response: IBusinessResponse) => {
        console.log(response);
        this.dataSource.data = response?.data?.results || [];
        this.dataSource.sort = this.sort;
        this.applySavedState();
        this.commonService.showSpinner = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.commonService.showSpinner = false;
      }
    });
  }

  openBusinessDetail(element: IBusiness) {
    this.router.navigate([Constants.APP_ROUTES.businessDetail(element.id)]);
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'trial':
        return 'trial';
      case 'live':
        return 'live';
      default:
        return 'default';
    }
  }

  getExpiryStatus(expiryDate: string): { label: string, class: string } {
    const today = new Date();
    const expiry = new Date(expiryDate);

    // Reset time
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diff = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diff < 0) return { label: 'Expired', class: 'expired' };
    if (diff === 0) return { label: 'Expires Today', class: 'today' };
    if (diff === 1) return { label: 'Expires Tomorrow', class: 'tomorrow' };
    if (diff <= 7) return { label: `Expires in ${diff} days`, class: 'warning' };

    return { label: 'Active', class: 'active' };
  }

}
